import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Grid,
  Checkbox,
  Alert,
  Snackbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BugReportIcon from '@mui/icons-material/BugReport';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { collection, query, getDocs, doc, updateDoc, addDoc, where, orderBy, onSnapshot, Timestamp, writeBatch, deleteField } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import { PaymentData, PaymentStatus, NewPaymentData } from '../types/payment';
import {
  buildMergedPayment,
  toMillis,
  validateMergeSelection
} from '../utils/paymentMerge';
import { useAppSelector } from '../store';
import VisibilityIcon from '@mui/icons-material/Visibility';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);

const formatTimestamp = (value: any, pattern = 'MM/dd/yyyy') => {
  const millis = toMillis(value);
  return millis ? format(new Date(millis), pattern) : 'N/A';
};

// Firestore allows at most 30 values in an `in` filter, and a merged payment can
// easily carry more invoices than that.
const IN_QUERY_LIMIT = 30;

const chunk = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

// Sets the status of every invoice belonging to a payment, in batches small
// enough for Firestore's `in` filter.
const updateInvoiceStatuses = async (invoiceNumbers: string[], status: string) => {
  const invoicesRef = collection(db, 'invoices');
  for (const invoiceNumbersChunk of chunk(invoiceNumbers, IN_QUERY_LIMIT)) {
    const snapshot = await getDocs(
      query(invoicesRef, where('invoiceNumber', 'in', invoiceNumbersChunk))
    );
    await Promise.all(snapshot.docs.map(invoiceDoc => updateDoc(invoiceDoc.ref, { status })));
  }
};

// Add getStatusColor function
const getStatusColor = (status: string): "warning" | "success" => {
  switch (status) {
    case 'PENDING':
      return 'warning'; // orange
    case 'PAID':
      return 'success'; // green
    default:
      return 'warning';
  }
};

interface PaymentDetailsDialogProps {
  open: boolean;
  payment: PaymentData | null;
  onClose: () => void;
}

const PaymentDetailsDialog: React.FC<PaymentDetailsDialogProps> = ({ open, payment, onClose }) => {
  if (!payment) return null;

  const mergedFrom = payment.mergedFrom || [];
  const isMerged = mergedFrom.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Payment Details
        {isMerged && (
          <Chip
            icon={<MergeTypeIcon />}
            label={`Merged from ${mergedFrom.length} payments`}
            color="info"
            size="small"
            sx={{ ml: 1 }}
          />
        )}
      </DialogTitle>
      <DialogContent>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Driver: {payment.driverName} ({payment.driverId})
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom>
            Created: {formatTimestamp(payment.createdAt, 'MM/dd/yyyy h:mm a')}
          </Typography>

          {isMerged && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Covers payments created {formatTimestamp(mergedFrom[0].createdAt)} –{' '}
              {formatTimestamp(mergedFrom[mergedFrom.length - 1].createdAt)}
            </Typography>
          )}

          <Typography variant="h6" mt={2}>
            Invoices ({payment.invoiceNumbers.length}):
          </Typography>
          {isMerged ? (
            // Keep each merged payment's loads visible as its own group so the
            // admin can still tell which payment report a load came from.
            mergedFrom.map((source, sourceIndex) => (
              <Box key={source.id || sourceIndex} mb={1}>
                <Typography variant="subtitle2" color="text.secondary" mt={1}>
                  {formatTimestamp(source.createdAt, 'MM/dd/yyyy h:mm a')} ·{' '}
                  {formatCurrency(source.totalAfterDeductions)} ({source.invoiceNumbers.length} loads)
                </Typography>
                <List dense disablePadding>
                  {source.invoiceNumbers.map((invoiceNumber, index) => (
                    <ListItem key={index} sx={{ pl: 3 }}>
                      <ListItemText primary={invoiceNumber} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))
          ) : (
            <List>
              {payment.invoiceNumbers.map((invoiceNumber, index) => (
                <ListItem key={index}>
                  <ListItemText primary={invoiceNumber} />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6">Deductions Breakdown:</Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Total Before Deductions" 
                secondary={formatCurrency(payment.totalBeforeDeductions)} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={`Percentage Cut (${payment.percentageCut}%)`}
                secondary={formatCurrency(payment.percentageCutAmount)} 
              />
            </ListItem>
            {payment.monthlyDeductions.map((deduction, index) => (
              <ListItem key={index}>
                <ListItemText 
                  primary={deduction.name}
                  secondary={`${formatCurrency(deduction.amount)}${deduction.description ? ` - ${deduction.description}` : ''}`}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" color="primary">
            Final Total: {formatCurrency(payment.totalAfterDeductions)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const DriverPayments: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<PaymentData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<PaymentData | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<string>('all');
  const [invoiceSearch, setInvoiceSearch] = useState<string>('');
  const [selectedForMerge, setSelectedForMerge] = useState<string[]>([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [paymentToUnmerge, setPaymentToUnmerge] = useState<PaymentData | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const isAdmin = user?.accountType === 'admin';

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    try {
      console.log('Fetching payments...', {
        isAdmin: user.accountType === 'admin',
        userDogtag: user.dogtag
      });
      const paymentsRef = collection(db, 'payments');
      
      // Create query based on user role
      const q = user.accountType === 'admin'
        ? query(paymentsRef, orderBy('createdAt', 'desc'))
        : query(
            paymentsRef,
            where('driverId', '==', user.dogtag),
            orderBy('createdAt', 'desc')
          );

      console.log('Query constraints:', {
        isAdmin: user.accountType === 'admin',
        userDogtag: user.dogtag,
        filters: (q as any)._query?.filters
      });

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const paymentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PaymentData[];
        // Payments that were merged into another payment are kept in Firestore
        // (so the merge can be undone) but are represented by the merged
        // payment from here on.
        setPayments(paymentsData.filter(payment => !payment.mergedIntoId));
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error fetching payments:', error);
      setLoading(false);
    }
  }, [user]);

  const handleCopyPaymentInfo = (payment: PaymentData) => {
    const text = `${payment.driverName}: ${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(payment.totalAfterDeductions)}`;
    navigator.clipboard.writeText(text);
  };

  const handleStatusChange = async (paymentId: string, newStatus: PaymentStatus) => {
    try {
      // Update payment status
      const paymentRef = doc(db, 'payments', paymentId);
      await updateDoc(paymentRef, { status: newStatus });

      // If status is changed to PAID, update all associated invoices
      if (newStatus === 'PAID') {
        // Find the payment to get associated invoice numbers
        const payment = payments.find(p => p.id === paymentId);
        if (payment && payment.invoiceNumbers.length > 0) {
          await updateInvoiceStatuses(payment.invoiceNumbers, 'PAID');
        }
      }
      // No need to refresh manually as onSnapshot will handle updates
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const handleDeleteClick = (payment: PaymentData) => {
    setPaymentToDelete(payment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (paymentToDelete) {
      try {
        // First, update all associated invoices back to pending
        if (paymentToDelete.invoiceNumbers.length === 0) return;

        await updateInvoiceStatuses(paymentToDelete.invoiceNumbers, 'pending');

        // Then delete the payment entry. For a merged payment the originals it
        // replaced go with it, otherwise they would stay hidden forever.
        const batch = writeBatch(db);
        (paymentToDelete.mergedSourceIds || []).forEach(sourceId => {
          batch.delete(doc(db, 'payments', sourceId));
        });
        batch.delete(doc(db, 'payments', paymentToDelete.id));
        await batch.commit();
        // No need to refresh manually as onSnapshot will handle updates

        setDeleteDialogOpen(false);
        setPaymentToDelete(null);
      } catch (error) {
        console.error('Error deleting payment:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete payment. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPaymentToDelete(null);
  };

  // Get unique drivers for filter dropdown
  const uniqueDrivers = Array.from(
    new Set(payments.map(p => p.driverId))
  ).map(driverId => {
    const payment = payments.find(p => p.driverId === driverId);
    return {
      id: driverId,
      name: payment?.driverName || driverId
    };
  }).sort((a, b) => a.name.localeCompare(b.name));

  // Filter payments based on selected driver and invoice search
  const filteredPayments = payments.filter(payment => {
    const matchesDriver = selectedDriver === 'all' || payment.driverId === selectedDriver;
    const matchesInvoice = invoiceSearch === '' || 
      payment.invoiceNumbers.some(inv => 
        inv.toLowerCase().includes(invoiceSearch.toLowerCase())
      );
    return matchesDriver && matchesInvoice;
  });

  /* ---------------------------- Merging payments --------------------------- */

  // Selection order does not matter, but keeping the merge input in list order
  // makes the preview match what the admin ticked.
  const selectedPayments = payments.filter(payment => selectedForMerge.includes(payment.id));
  const mergeDriverId = selectedPayments[0]?.driverId;
  const mergeDriverName = selectedPayments[0]?.driverName;

  // Merging only makes sense within one driver, so once one payment is picked
  // every other driver's payments are locked out.
  const canSelectForMerge = (payment: PaymentData) =>
    !mergeDriverId || payment.driverId === mergeDriverId;

  const selectablePayments = filteredPayments.filter(canSelectForMerge);
  const allSelectableSelected =
    selectablePayments.length > 0 &&
    selectablePayments.every(payment => selectedForMerge.includes(payment.id));
  // "Select all" is only offered when it cannot produce a cross-driver selection.
  const visibleDriverCount = new Set(filteredPayments.map(payment => payment.driverId)).size;
  const canSelectAll =
    selectablePayments.length > 0 && (Boolean(mergeDriverId) || visibleDriverCount === 1);

  // Payments hidden by the filters stay selected (the admin often searches to
  // find the next payment to merge), but ones that no longer exist are dropped.
  useEffect(() => {
    setSelectedForMerge(prev => {
      const existingIds = new Set(payments.map(payment => payment.id));
      const next = prev.filter(id => existingIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [payments]);

  const hiddenSelectionCount =
    selectedPayments.length - selectedPayments.filter(payment =>
      filteredPayments.some(visible => visible.id === payment.id)
    ).length;

  const handleToggleMergeSelection = (payment: PaymentData) => {
    setSelectedForMerge(prev =>
      prev.includes(payment.id)
        ? prev.filter(id => id !== payment.id)
        : [...prev, payment.id]
    );
  };

  const handleToggleSelectAll = () => {
    setSelectedForMerge(allSelectableSelected ? [] : selectablePayments.map(p => p.id));
  };

  const mergeValidation = validateMergeSelection(selectedPayments);
  const mergePreview = (() => {
    if (!mergeValidation.valid) return null;
    try {
      return buildMergedPayment(selectedPayments);
    } catch (error) {
      console.error('Error building merge preview:', error);
      return null;
    }
  })();

  const handleMergeConfirm = async () => {
    if (!mergeValidation.valid) return;

    setMerging(true);
    try {
      const now = Timestamp.now();
      const mergedPayment = buildMergedPayment(selectedPayments, now);

      // One batch so the merged payment and the originals it replaces can never
      // both be visible (or both be missing).
      const batch = writeBatch(db);
      const mergedRef = doc(collection(db, 'payments'));
      batch.set(mergedRef, mergedPayment);
      selectedPayments.forEach(payment => {
        batch.update(doc(db, 'payments', payment.id), {
          mergedIntoId: mergedRef.id,
          mergedIntoAt: now
        });
      });
      await batch.commit();

      setMergeDialogOpen(false);
      setSelectedForMerge([]);
      setSnackbar({
        open: true,
        message: `Merged ${selectedPayments.length} payments for ${mergedPayment.driverName}.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error merging payments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to merge payments. Please try again.',
        severity: 'error'
      });
    } finally {
      setMerging(false);
    }
  };

  const handleUnmergeConfirm = async () => {
    if (!paymentToUnmerge) return;

    const sourceIds = paymentToUnmerge.mergedSourceIds || [];
    if (sourceIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'This payment does not have the original payments on record and cannot be unmerged.',
        severity: 'error'
      });
      setPaymentToUnmerge(null);
      return;
    }

    setMerging(true);
    try {
      const batch = writeBatch(db);
      sourceIds.forEach(sourceId => {
        batch.update(doc(db, 'payments', sourceId), {
          mergedIntoId: deleteField(),
          mergedIntoAt: deleteField()
        });
      });
      batch.delete(doc(db, 'payments', paymentToUnmerge.id));
      await batch.commit();

      setSnackbar({
        open: true,
        message: `Restored ${sourceIds.length} payments for ${paymentToUnmerge.driverName}.`,
        severity: 'success'
      });
      setPaymentToUnmerge(null);
    } catch (error) {
      console.error('Error unmerging payment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to unmerge payment. Please try again.',
        severity: 'error'
      });
    } finally {
      setMerging(false);
    }
  };

  const handleDebug = async () => {
    try {
      const fakePayment: NewPaymentData = {
        createdAt: Timestamp.now(),
        driverName: 'Alex',
        driverId: 'ALEX001',
        invoiceNumbers: ['INV-2024-001', 'INV-2024-002', 'INV-2024-003'],
        totalBeforeDeductions: 2500.00,
        totalAfterDeductions: 1875.00,
        percentageCut: 25,
        percentageCutAmount: 625.00,
        monthlyDeductions: [
          {
            name: 'Insurance',
            amount: 150.00,
            description: 'Monthly insurance premium'
          },
          {
            name: 'Equipment Fee',
            amount: 75.00,
            description: 'Equipment maintenance fee'
          }
        ],
        status: 'PENDING'
      };

      await addDoc(collection(db, 'payments'), fakePayment);
      console.log('Debug payment created successfully');
    } catch (error) {
      console.error('Error creating debug payment:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 3 
      }}>
        <Typography variant="h5" component="h1">
          {user?.accountType === 'admin' ? 'Driver Payments' : 'My Payments'}
        </Typography>
        {user?.accountType === 'admin' && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<BugReportIcon />}
              onClick={handleDebug}
            >
              Debug
            </Button>
          </Box>
        )}
      </Box>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          {user?.accountType === 'admin' && (
            <FormControl sx={{ minWidth: 200, flex: 1 }} size="small">
              <InputLabel>Filter by Driver</InputLabel>
              <Select
                value={selectedDriver}
                label="Filter by Driver"
                onChange={(e) => setSelectedDriver(e.target.value)}
              >
                <MenuItem value="all">All Drivers</MenuItem>
                {uniqueDrivers.map(driver => (
                  <MenuItem key={driver.id} value={driver.id}>
                    {driver.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            sx={{ minWidth: 200, flex: 1 }}
            size="small"
            label="Search Invoice"
            placeholder="Enter invoice number..."
            value={invoiceSearch}
            onChange={(e) => setInvoiceSearch(e.target.value)}
          />
        </Box>
      </Paper>

      {/* Merge Selection Bar */}
      {isAdmin && selectedForMerge.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
            borderLeft: 4,
            borderColor: 'primary.main'
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1">
              {selectedForMerge.length} payment{selectedForMerge.length === 1 ? '' : 's'} selected
              {mergeDriverName ? ` for ${mergeDriverName}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedPayments.reduce((sum, p) => sum + (p.invoiceNumbers?.length || 0), 0)} loads ·{' '}
              {formatCurrency(
                selectedPayments.reduce((sum, p) => sum + (p.totalAfterDeductions || 0), 0)
              )}{' '}
              after deductions
              {hiddenSelectionCount > 0 &&
                ` · ${hiddenSelectionCount} not shown under the current filters`}
            </Typography>
          </Box>
          <Button onClick={() => setSelectedForMerge([])}>Clear</Button>
          <Tooltip title={mergeValidation.error || ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<MergeTypeIcon />}
                disabled={!mergeValidation.valid}
                onClick={() => setMergeDialogOpen(true)}
              >
                Merge Payments
              </Button>
            </span>
          </Tooltip>
        </Paper>
      )}

      {filteredPayments.length === 0 ? (
        <Typography color="text.secondary" align="center" py={4}>
          {invoiceSearch || selectedDriver !== 'all' 
            ? 'No payments match your filters' 
            : 'No payments found'}
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {isAdmin && (
                  <TableCell padding="checkbox">
                    <Tooltip
                      title={
                        canSelectAll
                          ? 'Select all payments shown for merging'
                          : 'Filter by a driver to select every payment at once'
                      }
                    >
                      <span>
                        <Checkbox
                          checked={allSelectableSelected}
                          indeterminate={selectedForMerge.length > 0 && !allSelectableSelected}
                          disabled={!canSelectAll}
                          onChange={handleToggleSelectAll}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell>Created Date</TableCell>
                {user?.accountType === 'admin' && <TableCell>Driver</TableCell>}
                <TableCell>Total Before Deductions</TableCell>
                <TableCell>Total After Deductions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.map((payment) => (
                <TableRow 
                  key={payment.id} 
                  hover 
                  onClick={() => {
                    setSelectedPayment(payment);
                    setDialogOpen(true);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  {isAdmin && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Tooltip
                        title={
                          canSelectForMerge(payment)
                            ? ''
                            : `Only ${mergeDriverName}'s payments can be merged with the current selection`
                        }
                      >
                        <span>
                          <Checkbox
                            checked={selectedForMerge.includes(payment.id)}
                            disabled={!canSelectForMerge(payment)}
                            onChange={() => handleToggleMergeSelection(payment)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>
                    {formatTimestamp(payment.createdAt)}
                  </TableCell>
                  {user?.accountType === 'admin' && (
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {payment.driverName}
                        {(payment.mergedFrom?.length || 0) > 0 && (
                          <Chip
                            icon={<MergeTypeIcon />}
                            label={`${payment.mergedFrom?.length} merged`}
                            color="info"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(payment.totalBeforeDeductions)}
                  </TableCell>
                  <TableCell>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(payment.totalAfterDeductions)}
                  </TableCell>
                  <TableCell>
                    {user?.accountType === 'admin' ? (
                      <Select
                        value={payment.status}
                        onChange={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleStatusChange(payment.id, e.target.value as PaymentStatus);
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent row click
                        size="small"
                      >
                        <MenuItem value="PENDING">Pending</MenuItem>
                        <MenuItem value="PROCESSED">Processed</MenuItem>
                        <MenuItem value="PAID">Paid</MenuItem>
                      </Select>
                    ) : (
                      <Chip
                        label={payment.status}
                        color={
                          payment.status === 'PAID'
                            ? 'success'
                            : payment.status === 'PROCESSED'
                            ? 'info'
                            : 'warning'
                        }
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Tooltip title="Copy Payment Info">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyPaymentInfo(payment);
                          }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (payment.mergedSourceIds?.length || 0) > 0 && (
                        <Tooltip title="Unmerge Payment">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPaymentToUnmerge(payment);
                            }}
                          >
                            <CallSplitIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {user?.accountType === 'admin' && (
                        <Tooltip title="Delete Payment">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(payment);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PaymentDetailsDialog
        open={dialogOpen}
        payment={selectedPayment}
        onClose={() => {
          setDialogOpen(false);
          setSelectedPayment(null);
        }}
      />

      {/* Merge Confirmation Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={() => !merging && setMergeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Merge Payments</DialogTitle>
        <DialogContent>
          {!mergePreview ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {mergeValidation.error || 'These payments cannot be merged.'}
            </Alert>
          ) : (
            <Box>
              <Typography variant="body1" gutterBottom>
                {selectedPayments.length} payments for <strong>{mergePreview.driverName}</strong>{' '}
                ({mergePreview.driverId}) will become a single payment holding all{' '}
                {mergePreview.invoiceNumbers.length} loads and every deduction below.
              </Typography>

              {mergeValidation.warnings.map((warning, index) => (
                <Alert key={index} severity="warning" sx={{ mt: 1 }}>
                  {warning}
                </Alert>
              ))}

              <Typography variant="h6" mt={2}>Payments being merged:</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Loads</TableCell>
                      <TableCell align="right">Before Deductions</TableCell>
                      <TableCell align="right">After Deductions</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mergePreview.mergedFrom.map((source, index) => (
                      <TableRow key={source.id || index}>
                        <TableCell>{formatTimestamp(source.createdAt, 'MM/dd/yyyy h:mm a')}</TableCell>
                        <TableCell align="right">{source.invoiceNumbers.length}</TableCell>
                        <TableCell align="right">{formatCurrency(source.totalBeforeDeductions)}</TableCell>
                        <TableCell align="right">{formatCurrency(source.totalAfterDeductions)}</TableCell>
                        <TableCell>{source.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" mt={3}>Merged payment:</Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Loads"
                    secondary={mergePreview.invoiceNumbers.join(', ')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Total Before Deductions"
                    secondary={formatCurrency(mergePreview.totalBeforeDeductions)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`Percentage Cut (${mergePreview.percentageCut}%)`}
                    secondary={formatCurrency(mergePreview.percentageCutAmount)}
                  />
                </ListItem>
                {mergePreview.monthlyDeductions.map((deduction, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={deduction.name}
                      secondary={`${formatCurrency(deduction.amount)}${deduction.description ? ` - ${deduction.description}` : ''}`}
                    />
                  </ListItem>
                ))}
                <ListItem>
                  <ListItemText
                    primary="Status"
                    secondary={mergePreview.status}
                  />
                </ListItem>
              </List>

              <Typography variant="h6" color="primary">
                Final Total: {formatCurrency(mergePreview.totalAfterDeductions)}
              </Typography>

              <Alert severity="info" sx={{ mt: 2 }}>
                The original payments are kept on record and no invoice statuses change.
                You can undo this from the merged payment&apos;s unmerge button.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)} disabled={merging}>
            Cancel
          </Button>
          <Button
            onClick={handleMergeConfirm}
            color="primary"
            variant="contained"
            startIcon={merging ? <CircularProgress size={16} color="inherit" /> : <MergeTypeIcon />}
            disabled={merging || !mergePreview}
          >
            {merging ? 'Merging...' : 'Merge Payments'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unmerge Confirmation Dialog */}
      <Dialog open={Boolean(paymentToUnmerge)} onClose={() => !merging && setPaymentToUnmerge(null)}>
        <DialogTitle>Undo Merge</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete the merged payment and restore the{' '}
            {paymentToUnmerge?.mergedSourceIds?.length || 0} original payments for{' '}
            {paymentToUnmerge?.driverName}, each with the status it had before the merge.
            Invoice statuses are not changed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentToUnmerge(null)} disabled={merging}>
            Cancel
          </Button>
          <Button
            onClick={handleUnmergeConfirm}
            color="primary"
            variant="contained"
            disabled={merging}
          >
            {merging ? 'Unmerging...' : 'Unmerge'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete this payment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverPayments; 